import { useFlags } from "launchdarkly-react-client-sdk";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useFormContext } from "react-hook-form";

import ContentPasteOutlinedIcon from "@mui/icons-material/ContentPasteOutlined";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import useTheme from "@mui/material/styles/useTheme";

import { addYears } from "date-fns";

import { useAppDispatch, useAppSelector } from "app/hooks";
import CheckboxFormField from "components/checkbox-form-field";
import ErrorList from "components/error-list";
import ExpandableNotesField from "components/expandable-notes-field";
import PhysicianField from "components/physician-field";
import StaffMemberFormField from "components/staff-member-form-field";
import SubReferralSourceField from "components/sub-referral-source-field";
import useMergeTags from "components/word-processor-field/merge-tags/use-merge-tags.hook";
import { getTextWithMergeTagsValues } from "components/word-processor-field/text-templates/text-templates.utils";
import { setDataParams } from "components/word-processor-field/word-processor.slice";
import {
  REOCCURRENCE_PERIOD_TYPE_DAILY,
  REOCCURRENCE_PERIOD_TYPE_DAY_IN_WEEK_OF_MONTH,
  REOCCURRENCE_PERIOD_TYPE_MONTHLY
} from "constants/appointments";
import {
  APPOINTMENT_EMAIL_NOTIFICATION_FEATURE,
  APPOINTMENT_TYPE_DEFAULT_CHART_NOTE,
  ASSIGNED_APPOINTMENT_TAGS_FEATURE,
  DISPLAY_SUPERVISING_PROVIDER,
  RECURRING_APPOINTMENTS_FEATURE,
  SHOW_PATIENT_TAGS_ON_APPOINTMENT_FORM
} from "constants/feature-flags";
import { VIEW_OWN_SCHEDULED_ONLY } from "constants/permissions";
import {
  TEMPLATE_TYPE_NEW_APPOINTMENT_NOTE,
  TEMPLATE_TYPE_SNIPPET
} from "constants/templates";
import { selectCurrentPractice } from "features/auth/auth-slice";
import {
  selectPatientCreateAppointmentModeValues,
  selectReschedulingValues
} from "features/scheduling/scheduler-slice";
import { ErrorsState } from "hooks/use-errors.hook";
import useUserRoles from "hooks/use-user-roles.hook";
import { useGetPatientQuery } from "services/patients";
import { type AssignedTag } from "services/types/assigned-tag.type";
import type { Patient } from "services/types/patients.type";
import { MinimalStaffMember } from "services/types/staff.type";
import { type Tag } from "services/types/tags.type";
import { convertHtmlToText } from "utils/common";

import { AppointmentFormValues } from "../appointment-form.component";
import {
  AppointmentDatesField,
  AppointmentTypeField,
  AssignedAppointmentTagsField,
  CompanionField,
  PatientField,
  ReferralField,
  RoomField,
  StaffField
} from "../fields";
import AppointmentOptionalDateField from "../fields/appointment-dates-field/appointment-optional-date-field.component";
import ClinicField from "../fields/clinic-field.component";
import NotificationMethodField from "../fields/notifications-and-intake-forms-field.component";
import RecurrenceRuleEditorField from "../fields/recurrence-rule-editor-field.component";
import RecurringPeriodField from "../fields/recurring-period-field.component";
import ReferralSourceField from "../fields/referral-source-field.component";

type AppointmentFormViewProps = {
  defaultValues: AppointmentFormValues;
  nonFieldErrors: ErrorsState;
  isAppointmentInFinalState?: boolean;
  isAppointmentConfirmed?: boolean;
  multiClinic?: boolean;
  hidePatientField?: boolean;
  isPatientLocked?: boolean;
  tags?: AssignedTag[];
  onTagDelete?: (tag_id: number, index?: number) => void;
  onTagAdd?: (tag_id: number, tag: Tag) => void;
  onTagUpdate?: (tag: AssignedTag) => void;
  appointmentId?: number | null;
  recurrenceRuleEditorOpen?: boolean;
  setRecurrenceRuleEditorOpen?: (value: boolean) => void;
  nonRRuleRecurrence?: boolean;
  isSubReferralSourceRequired?: boolean;
};

const templateTypes = [
  TEMPLATE_TYPE_NEW_APPOINTMENT_NOTE,
  TEMPLATE_TYPE_SNIPPET
];

export default function AppointmentFormView({
  defaultValues,
  nonFieldErrors,
  isAppointmentConfirmed = false,
  multiClinic = false,
  hidePatientField,
  isPatientLocked = false,
  appointmentId,
  tags,
  onTagDelete,
  onTagAdd,
  onTagUpdate,
  recurrenceRuleEditorOpen,
  setRecurrenceRuleEditorOpen,
  nonRRuleRecurrence,
  isSubReferralSourceRequired = false
}: AppointmentFormViewProps) {
  const theme = useTheme();
  const currentPractice = useAppSelector(selectCurrentPractice);
  const [showOverlapWarning, setShowOverlapWarning] = React.useState(false);
  const [showAvailableWarning, setShowAvailableWarning] = React.useState(false);

  const flags = useFlags();
  const isEmailNotificationEnabled =
    flags[APPOINTMENT_EMAIL_NOTIFICATION_FEATURE];
  const showPatientTags = flags[SHOW_PATIENT_TAGS_ON_APPOINTMENT_FORM];
  const isAssignedAppointmentTagsFeature =
    flags[ASSIGNED_APPOINTMENT_TAGS_FEATURE];
  const displaySupervisingProvider = flags[DISPLAY_SUPERVISING_PROVIDER];

  const recurringAppointmentFeatureEnabled =
    flags[RECURRING_APPOINTMENTS_FEATURE];

  const { userPermissions } = useUserRoles();
  const viewOwnScheduleOnly = userPermissions[VIEW_OWN_SCHEDULED_ONLY];

  const [showNotesDialog, setShowNotesDialog] = useState(false);

  const { originalAppointmentData } = useAppSelector(selectReschedulingValues);
  const { returnToPath } = useAppSelector(
    selectPatientCreateAppointmentModeValues
  );

  useEffect(() => {
    if (
      nonFieldErrors?.nonFieldErrors?.some(
        (error) =>
          error.message ===
          "Staff member is not available for appointment (busy)"
      )
    ) {
      setShowOverlapWarning(true);
    }
  }, [nonFieldErrors]);

  const {
    setValue,
    control,
    clearErrors,
    trigger,
    watch,
    getFieldState,
    getValues
  } = useFormContext<AppointmentFormValues>();

  useEffect(() => {
    return clearErrors;
  }, [clearErrors]);

  const [
    patient,
    clinic,
    room,
    isRecurring,
    appointmentDate,
    appointmentType,
    notes,
    supervisingProvider
  ]: [
    Patient | undefined,
    Clinic | null | undefined,
    Room | null | undefined,
    boolean | null | undefined,
    Date | null | undefined,
    AppointmentType | null,
    string,
    MinimalStaffMember | null | undefined
  ] = watch([
    "patient",
    "clinic",
    "room",
    "is_recurring",
    "appointment_date",
    "appointment_type",
    "notes",
    "supervising_provider"
  ]);

  useEffect(() => {
    trigger("event_end_date");
  }, [trigger, isRecurring]);

  useEffect(() => {
    if (!supervisingProvider) {
      setValue("is_direct_supervisor", false);
    }
  }, [supervisingProvider, setValue]);

  const maxDate = useMemo(() => {
    return isRecurring && appointmentDate ? addYears(appointmentDate, 1) : null;
  }, [isRecurring, appointmentDate]);

  const { data: patientPreferredFields } = useGetPatientQuery(
    {
      patientId: patient?.id || -1,
      query:
        "{preferred_clinic{id,timezone,name,noah_provider},preferred_provider}"
    },
    {
      skip: !patient?.id
    }
  );

  const boxStyles = useMemo(() => {
    return {
      boxShadow: theme.shadows[3],
      borderRadius: "6px",
      padding: 2,
      pb: 3,
      background: "#fff"
    };
  }, [theme]);

  const preferredClinic = patient?.preferred_clinic?.practice
    ? patient?.preferred_clinic
    : patientPreferredFields?.preferred_clinic;

  const preferredProvider =
    patient?.preferred_provider || patientPreferredFields?.preferred_provider;

  const updatePatientData = useCallback(
    (
      patient: Patient | null,
      options?: { isNewlyCreated?: boolean }
    ) => {
      if (getValues("id") === null) {
        if (
          !returnToPath &&
          !originalAppointmentData &&
          !getValues("staff_member") &&
          patient?.preferred_provider &&
          !getFieldState("clinic").isDirty
        ) {
          setValue(
            "staff_member",
            patient?.preferred_provider as MinimalStaffMember,
            {
              shouldDirty: true,
              shouldValidate: true
            }
          );
        }

        if (
          multiClinic &&
          patient?.preferred_clinic &&
          patient?.preferred_clinic?.practice?.id === currentPractice?.id &&
          !getFieldState("clinic").isDirty &&
          !defaultValues.clinicIdToOverride
        ) {
          setValue("clinic", patient?.preferred_clinic, {
            shouldDirty: true,
            shouldValidate: true
          });
        }

        trigger(["staff_member", "clinic"]);
      }

      const shouldUpdateReferralSource =
        !options?.isNewlyCreated || Boolean(patient?.referral_source?.id);
      if (shouldUpdateReferralSource) {
        setValue(
          "referral_source",
          patient?.referral_source?.id
            ? String(patient.referral_source.id)
            : "",
          {
            shouldDirty: true,
            shouldValidate: true
          }
        );
        const subReferralSourceId =
          patient?.sub_referral_source?.id &&
          patient?.sub_referral_source?.parent?.id ===
            patient?.referral_source?.id
            ? patient?.sub_referral_source?.id
            : "";
        setValue("sub_referral_source", subReferralSourceId, {
          shouldDirty: true,
          shouldValidate: true
        });
        setValue(
          "sub_referral_source_object",
          subReferralSourceId ? patient?.sub_referral_source : null,
          {
            shouldDirty: true,
            shouldValidate: true
          }
        );
      }
    },
    [
      returnToPath,
      originalAppointmentData,
      getValues,
      setValue,
      getFieldState,
      multiClinic,
      currentPractice?.id,
      defaultValues.clinicIdToOverride,
      trigger
    ]
  );

  useEffect(() => {
    if (clinic?.id && room?.clinic?.id && clinic.id !== room.clinic.id) {
      setValue("room", null, {
        shouldDirty: true,
        shouldValidate: true
      });
    }
  }, [setValue, clinic, room]);

  const showRRuleForNewAppointment = !appointmentId;

  const showRRuleRecurringField = Boolean(
    !nonRRuleRecurrence || showRRuleForNewAppointment
  );

  const fieldIdRef = useRef<string | undefined>("appointment-form-notes");
  const dispatch = useAppDispatch();

  const { isReady: isMergeTagsDataSourceReady, getMergeTagValue } =
    useMergeTags({
      fieldIdRef,
      allowMergeTags: true,
      allowInsertMergeTagsValues: true,
      allowHtml: true,
      patientId: patient?.id
    });

  // Set up Redux store with patient data for merge tags
  useEffect(() => {
    if (patient?.id && fieldIdRef.current) {
      dispatch(
        setDataParams({
          id: fieldIdRef.current,
          patientId: patient.id
        })
      );
    }
  }, [dispatch, patient?.id]);

  const isDefaultChartNoteEnabled = flags[APPOINTMENT_TYPE_DEFAULT_CHART_NOTE];
  const defaultNote = isDefaultChartNoteEnabled
    ? appointmentType?.default_appointment_notes
    : appointmentType?.default_chart_note;
  const isNotesDirty = getFieldState("notes").isDirty;
  const allowInsertDefaultNotes =
    !appointmentId ||
    (appointmentId && !notes && getFieldState("appointment_type").isDirty) ||
    (appointmentId && getFieldState("patient").isDirty);

  useEffect(() => {
    if (
      allowInsertDefaultNotes &&
      isMergeTagsDataSourceReady &&
      patient?.id &&
      clinic?.id &&
      !isNotesDirty
    ) {
      const result = getTextWithMergeTagsValues({
        text: defaultNote ?? "",
        getMergeTagValue,
        insertMergeTagValues: true,
        allowHtml: false
      });
      setValue("notes", convertHtmlToText(result), { shouldDirty: false });
    }
  }, [
    allowInsertDefaultNotes,
    isMergeTagsDataSourceReady,
    defaultNote,
    getMergeTagValue,
    setValue,
    patient?.id,
    clinic?.id,
    isNotesDirty
  ]);

  return (
    <Stack spacing={2}>
      <Stack spacing={2} sx={boxStyles}>
        {!hidePatientField && (
          <PatientField
            control={control}
            disabled={isAppointmentConfirmed || isPatientLocked}
            onOptionSelected={updatePatientData}
            showPatientTags={showPatientTags}
            appointmentId={appointmentId}
          />
        )}
        <AppointmentTypeField control={control} />
        <Stack flexDirection="row" gap={2}>
          {multiClinic && (
            <Box width="50%" flexGrow={1}>
              <ClinicField
                control={control}
                preferredClinic={preferredClinic}
                required
              />
            </Box>
          )}
          <Box width="50%" flexGrow={1}>
            <RoomField control={control} showIcon={!multiClinic} />
          </Box>
        </Stack>
        <StaffField
          control={control}
          showAvailableWarning={showAvailableWarning}
          setShowAvailableWarning={setShowAvailableWarning}
          showActiveOnly={Boolean(
            !defaultValues.staff_member || defaultValues.staff_member?.is_active
          )}
          preferredProvider={preferredProvider}
          disabled={viewOwnScheduleOnly}
        />
        {displaySupervisingProvider && (
          <Stack direction="row" alignItems="flex-end" gap={2} pl="36px">
            <StaffMemberFormField
              name="supervising_provider"
              control={control}
              label="Supervising Provider"
              limitToCurrentClinic
              sx={{}}
            />

            <FormControlLabel
              label="Is Direct Supervisor"
              control={
                <CheckboxFormField
                  testId="is_direct_supervisor"
                  name="is_direct_supervisor"
                  control={control}
                  disabled={!supervisingProvider}
                />
              }
              sx={{ minWidth: "max-content" }}
            />
          </Stack>
        )}
        <Box>
          <AppointmentDatesField
            control={control}
            setValue={setValue}
            showAvailableWarning={showAvailableWarning}
            showOverlapWarning={showOverlapWarning}
            defaultValues={{
              appointment_date: defaultValues.appointment_date,
              start_time: defaultValues.start_time,
              end_time: defaultValues.end_time
            }}
            label="Date"
            fromLabel="from"
            toLabel="to"
          />
        </Box>
        {recurringAppointmentFeatureEnabled && (
          <>
            {isRecurring && (
              <AppointmentOptionalDateField
                label="Date End"
                name="event_end_date"
                control={control}
                setValue={setValue}
                required
                maxDate={maxDate || undefined}
              />
            )}

            {showRRuleRecurringField ? (
              <RecurrenceRuleEditorField
                control={control}
                setValue={setValue}
                open={recurrenceRuleEditorOpen}
                setOpen={setRecurrenceRuleEditorOpen}
                sx={{ ml: 6 }}
              />
            ) : (
              <RecurringPeriodField
                control={control}
                setValue={setValue}
                recurrenceReadonly={Boolean(isRecurring)}
                exclude={[
                  REOCCURRENCE_PERIOD_TYPE_MONTHLY,
                  REOCCURRENCE_PERIOD_TYPE_DAILY,
                  REOCCURRENCE_PERIOD_TYPE_DAY_IN_WEEK_OF_MONTH
                ]}
              />
            )}
          </>
        )}
      </Stack>
      {patient && isEmailNotificationEnabled && (
        <Stack sx={boxStyles}>
          <NotificationMethodField patient={patient} />
        </Stack>
      )}

      <Stack spacing={2} sx={boxStyles}>
        <Stack flexDirection="row" gap={2}>
          <Box width="50%">
            <ReferralSourceField
              control={control}
              afterUpdate={() => {
                setValue("sub_referral_source", "", {
                  shouldDirty: true,
                  shouldValidate: true
                });
                setValue("sub_referral_source_object", null, {
                  shouldDirty: true,
                  shouldValidate: true
                });
              }}
            />
          </Box>

          <Box width="50%">
            <SubReferralSourceField
              control={control}
              required={isSubReferralSourceRequired}
            />
          </Box>
        </Stack>

        <Stack flexDirection="row" gap={2}>
          <Box width="50%">
            <PhysicianField
              label="Referring Physician"
              name="referring_physician"
              control={control}
              allowNew
              showIcon
            />
          </Box>

          <Box width="50%">
            <ReferralField control={control} />
          </Box>
        </Stack>
        <CompanionField control={control} />
      </Stack>
      {isAssignedAppointmentTagsFeature && (
        <Box sx={boxStyles}>
          <AssignedAppointmentTagsField
            tags={tags}
            onDelete={onTagDelete}
            onUpdate={onTagUpdate}
            onAdd={onTagAdd}
          />
        </Box>
      )}

      <Box sx={boxStyles}>
        <Stack
          direction="row"
          width="100%"
          alignItems="center"
          justifyContent="space-between"
        >
          <ContentPasteOutlinedIcon sx={{ mt: 0.5, mr: 1.5 }} />
          <Box width="100%">
            <ExpandableNotesField
              control={control}
              name="notes"
              label="Appointment Notes"
              placeholder="Appointment Notes"
              showMemoDialog={showNotesDialog}
              setShowMemoDialog={setShowNotesDialog}
              setValue={setValue}
              templateTypes={templateTypes}
              sx={{ "& textarea": { minHeight: 50 } }}
              mergeTagsPatientId={patient?.id}
              expandButtonLabel="Expand notes"
              enableExpandButton
              patientNotSelectedWarning="Please, select a patient to activate templates and merge tags"
            />
          </Box>
        </Stack>
      </Box>
      <ErrorList errors={nonFieldErrors} />
    </Stack>
  );
}
